import { createBrandConfigStore, type PublishedConfig } from '@t-soft-io/retail-shared/brand'
import { isLocalDevHost, mqttConfig, platformEnvironment } from '@t-soft-io/retail-shared/platform'
import { clearBrandSession } from '../utils/brandSession'
import { fetchImageDataUri } from './fetchImageDataUri'
import {
  localhostConfig,
  devConfig,
  prodConfig,
  winnersGWConfig,
  bwinnersZMConfig,
  bwinnersSLConfig,
  bwinnersCOMConfig,
  t3betConfig,
  mellybetLRConfig
} from './domains'
import { GLOBAL_CONFIG } from './constants/global.config'
import { DomainConfig } from './types'
import {
  DEFAULT_COUNTRY,
  DEV_SELECTOR_HOST,
  PROD_COUNTRY_OVERRIDES,
  PROD_SELECTOR_HOST
} from './environmentSelector/constants'
import { getCurrentCountryOverrides } from './environmentSelector/utils'

const PRODUCTION_CONFIGS: Record<string, DomainConfig> = {
  'pos.bwinners.co.zw': prodConfig,
  'pos.winners.gw': winnersGWConfig,
  'pos.guinea.prod.dev.t-soft.io': winnersGWConfig,
  'pos.bwinners.com.zm': bwinnersZMConfig,
  'pos.winners.sl': bwinnersSLConfig,
  'pos.bwinners.sl': bwinnersSLConfig,
  'pos.bwinners.com': bwinnersCOMConfig,
  'pos.ta3bet.biz': t3betConfig,
  'pos.mellybet.com.lr': mellybetLRConfig
}

// Base for the multi-country prod selector: prod API + MQTT (GLOBAL_CONFIG); the
// country's APP_KEY/CLIENT_ID come from the selector, defaulting to DEFAULT_COUNTRY.
const prodSelectorBase: DomainConfig = {
  ...GLOBAL_CONFIG,
  ...PROD_COUNTRY_OVERRIDES[DEFAULT_COUNTRY]
}

const hostnameConfiguration = (host: string): DomainConfig => {
  if (PRODUCTION_CONFIGS[host]) return PRODUCTION_CONFIGS[host]

  if (host === PROD_SELECTOR_HOST) {
    return { ...prodSelectorBase, ...getCurrentCountryOverrides() }
  }

  const base = host === DEV_SELECTOR_HOST ? devConfig : localhostConfig
  return { ...base, ...getCurrentCountryOverrides() }
}

const hasHostnameConfiguration = (host: string): boolean =>
  host in PRODUCTION_CONFIGS ||
  host === PROD_SELECTOR_HOST ||
  host === DEV_SELECTOR_HOST ||
  isLocalDevHost(host)

interface PosAppConfig {
  receiptLogo?: string
}

const fromPublished = (cfg: PublishedConfig<PosAppConfig>): DomainConfig => ({
  API_BASE_URL: cfg.api.baseUrl,
  ...mqttConfig(platformEnvironment(window.location.hostname)),
  APP_KEY: cfg.appKey,
  CLIENT_ID: cfg.clientId,
  BRAND_NAME: cfg.brand.name,
  LOGO_SRC: cfg.branding?.logo?.src,
  RECEIPT_LOGO_URL: cfg.receiptLogo
})

const store = createBrandConfigStore<PosAppConfig, DomainConfig>({
  app: 'pos',
  fromPublished,
  onBrandChange: clearBrandSession,
  fallback: () => {
    const { hostname } = window.location
    return hasHostnameConfiguration(hostname) ? hostnameConfiguration(hostname) : null
  }
})

// Filled in place by loadAppConfiguration, so earlier imports see the loaded values.
export const APP_CONFIGURATION: DomainConfig = {
  ...hostnameConfiguration(window.location.hostname)
}

// Hosts with no published config (Firebase) fall back to the hostname table.
const BOOT_FROM_PUBLISHED_CONFIG = true

interface LoadAppConfigurationOptions {
  published?: boolean
}

export const loadAppConfiguration = async ({
  published = BOOT_FROM_PUBLISHED_CONFIG
}: LoadAppConfigurationOptions = {}): Promise<DomainConfig> => {
  if (!published) {
    return Object.assign(APP_CONFIGURATION, hostnameConfiguration(window.location.hostname))
  }
  const resolved = { ...(await store.load()) }
  if (resolved.RECEIPT_LOGO_URL) {
    resolved.RECEIPT_LOGO = await fetchImageDataUri(resolved.RECEIPT_LOGO_URL)
  }
  for (const key of Object.keys(APP_CONFIGURATION)) {
    delete APP_CONFIGURATION[key as keyof DomainConfig]
  }
  return Object.assign(APP_CONFIGURATION, resolved)
}

export const isPublishedConfiguration = store.isPublished

export const getSharedBrands = store.sharedBrands

// True when the host resolves its tenant via the country selector (the prod/dev selector
// hosts, localhost, or any unregistered host) rather than a fixed single-country config.
// The fixed prod hosts short-circuit above and ignore the selector, so it must be hidden
// there to avoid offering a dropdown that does nothing. A published config hides it too.
export const isSelectorDrivenHost = (): boolean =>
  !isPublishedConfiguration() && !PRODUCTION_CONFIGS[window.location.hostname]

export const getFixedHostCountry = (): string | undefined =>
  PRODUCTION_CONFIGS[window.location.hostname]?.COUNTRY
